/**
 * Comprehensive Type Definitions for TerraMatrix Visual Feedback System
 * Four-State Architecture with Hardware-Accelerated Animations
 */

import { Position, ComponentType } from "@/types";
import { Rectangle } from "@/types/canvas-legacy-types";

// ============================================================================
// CORE VISUAL FEEDBACK TYPES
// ============================================================================

/**
 * Four-State Visual Feedback System with Ghost Drop Indicator Extensions
 * Each state provides progressive visual enhancement with multi-modal feedback
 */
export enum VisualFeedbackState {
  EMPTY = "empty", // Empty/Ready state - subtle grid, help prompts
  READY = "ready", // Ready/Hover state - drop zone activation, previews
  ACTIVE = "active", // Active/Dragging state - elevation, guides, tracking
  SUCCESS = "success", // Success/Placement state - confirmation, settling
  ERROR = "error", // Error state - shake, recovery guidance

  // Ghost Drop Indicator States
  GHOST_PREVIEW = "ghost_preview", // Ghost component preview during drag
  GHOST_VALID = "ghost_valid", // Valid drop position with ghost indicator
  GHOST_INVALID = "ghost_invalid", // Invalid drop position with ghost indicator
  GHOST_SNAPPING = "ghost_snapping", // Ghost snapping to grid/guides
  GHOST_TRANSITION = "ghost_transition", // Ghost transitioning to final position
}

/**
 * Visual feedback configuration for different interaction contexts
 */
export interface VisualFeedbackConfig {
  state: VisualFeedbackState;
  intensity: "subtle" | "normal" | "prominent";
  duration: number;
  easing: string;
  hardwareAccelerated: boolean;
  reducedMotion: boolean;
  multiModal: boolean; // Include haptic, audio cues
}

/**
 * Animation performance specifications for 60fps targets
 */
export interface AnimationPerformanceSpec {
  targetFPS: 60;
  maxFrameDrops: number; // Maximum acceptable frame drops per second
  gpuLayerOptimization: boolean;
  willChangeManagement: boolean;
  animationPooling: boolean;
  batchProcessing: boolean;
}

// ============================================================================
// FOUR-STATE SYSTEM SPECIFICATIONS
// ============================================================================

/**
 * Empty/Ready State Configuration
 * Subtle visual cues indicating available drop areas and component palette
 */
export interface EmptyStateConfig {
  gridPattern: {
    enabled: boolean;
    size: number; // Grid cell size in pixels
    opacity: number;
    color: string;
    animationCycle: number; // Animation cycle duration in seconds
  };
  helpPrompts: {
    enabled: boolean;
    text: string;
    position: "center" | "top" | "bottom";
    fadeInDelay: number;
  };
  paletteHighlight: {
    enabled: boolean;
    glowIntensity: number;
    pulseCycle: number;
  };
}

/**
 * Ready/Hover State Configuration
 * Active drop zone indicators and component previews
 */
export interface ReadyStateConfig {
  dropZone: {
    borderAnimation: "flow" | "pulse" | "static";
    borderColor: string;
    backgroundOpacity: number;
    animationSpeed: number; // Animation speed in seconds
  };
  ghostPreview: {
    enabled: boolean;
    opacity: number;
    shadowIntensity: number;
    snapIndicators: boolean;
  };
  alignmentGuides: {
    enabled: boolean;
    color: string;
    thickness: number;
    magneticThreshold: number; // Pixels for magnetic alignment
  };
  invalidZones: {
    enabled: boolean;
    overlayColor: string;
    overlayOpacity: number;
    prohibitionIcon: boolean;
  };
}

/**
 * Ghost Drop Indicator Configuration
 * Advanced ghost preview system with real-time positioning
 */
export interface GhostIndicatorConfig {
  // Ghost appearance settings
  opacity: {
    valid: number; // Opacity for valid drop positions (0.7)
    invalid: number; // Opacity for invalid drop positions (0.4)
    transition: number; // Opacity during transitions (0.5)
  };

  // Visual feedback styling
  styling: {
    blendMode: "normal" | "multiply" | "screen" | "overlay";
    borderStyle: "dashed" | "solid" | "dotted";
    borderWidth: number;
    borderColor: {
      valid: string; // Green for valid drops
      invalid: string; // Red for invalid drops
      snapping: string; // Blue for grid snapping
    };
    shadowSettings: {
      blur: number;
      spread: number;
      color: string;
      opacity: number;
    };
  };

  // Animation configuration
  animations: {
    positionTransition: {
      duration: number; // Position update animation duration (50ms)
      easing: string; // CSS timing function
      threshold: number; // Minimum movement to trigger animation
    };
    stateTransition: {
      duration: number; // State change animation duration (150ms)
      easing: string;
    };
    snapFeedback: {
      enabled: boolean;
      intensity: number; // Scale factor for snap animation
      duration: number; // Snap animation duration
    };
  };

  // Performance optimization
  performance: {
    updateThrottle: number; // Throttle position updates (16ms for 60fps)
    useRequestAnimationFrame: boolean;
    gpuAcceleration: boolean;
    willChangeOptimization: boolean;
    cullingDistance: number; // Hide ghost when too far from drop zones
  };

  // Accessibility features
  accessibility: {
    announcePosition: boolean;
    announceValidity: boolean;
    screenReaderCompatible: boolean;
    highContrastMode: boolean;
    reducedMotionSupport: boolean;
  };

  // Component-specific previews
  componentPreviews: {
    [ComponentType.TEXT]: {
      showSampleText: boolean;
      previewContent: string;
    };
    [ComponentType.BUTTON]: {
      showButtonStyle: boolean;
      previewText: string;
    };
    [ComponentType.CONTAINER]: {
      showBoundaries: boolean;
      showChildrenPlaceholders: boolean;
    };
    [ComponentType.IMAGE]: {
      showPlaceholder: boolean;
      placeholderIcon: string;
    };
    [ComponentType.CHART]: {
      showMiniPreview: boolean;
      previewType: "bars" | "line" | "pie";
    };
  };

  // Grid and guide integration
  gridIntegration: {
    enabled: boolean;
    showSnapGuides: boolean;
    guideColor: string;
    guideThickness: number;
    magneticDistance: number; // Distance for magnetic snapping
    showGridDots: boolean;
  };

  // Multi-viewport support
  viewportAdaptation: {
    enabled: boolean;
    scaleFactors: {
      mobile: number;
      tablet: number;
      desktop: number;
    };
    touchTargetMinimum: number; // Minimum touch target size
    responsivePreview: boolean;
  };
}

/**
 * Active/Dragging State Configuration
 * Enhanced visual feedback during active drag operations
 */
export interface ActiveStateConfig {
  componentTransform: {
    scale: number; // Scale factor during drag
    rotation: number; // Rotation in degrees
    elevation: number; // Z-index elevation
    shadowBlur: number;
    shadowOpacity: number;
    filterBrightness: number;
  };
  originMarker: {
    enabled: boolean;
    size: number;
    color: string;
    animationType: "ping" | "pulse" | "ripple";
  };
  realTimeGuides: {
    enabled: boolean;
    snapThreshold: number;
    color: string;
    opacity: number;
    animationSpeed: number;
  };
  autoScroll: {
    enabled: boolean;
    edgeThreshold: number; // Pixels from edge to trigger
    scrollSpeed: "adaptive" | number;
    maxSpeed: number;
  };
}

/**
 * Success/Placement State Configuration
 * Confirmation feedback and settling animations
 */
export interface SuccessStateConfig {
  placementAnimation: {
    duration: number;
    easing: string;
    scaleSequence: number[]; // Scale keyframes
    opacitySequence: number[]; // Opacity keyframes
    shadowSequence: string[]; // Box-shadow keyframes
  };
  successIndicator: {
    enabled: boolean;
    icon: string; // Checkmark, etc.
    size: number;
    color: string;
    position: "top-right" | "center" | "top-left";
    animationType: "bounce" | "scale" | "rotate";
    displayDuration: number;
  };
  settlingEffect: {
    enabled: boolean;
    finalShadow: string;
    settleSpeed: number;
  };
  propertyPanelActivation: {
    enabled: boolean;
    activationDelay: number;
    transitionType: "slide" | "fade" | "scale";
  };
}

// ============================================================================
// HARDWARE-ACCELERATED ANIMATION FRAMEWORK
// ============================================================================

/**
 * GPU-accelerated animation properties
 * Uses only transform and opacity for optimal performance
 */
export interface GPUAnimationProperties {
  transform: string; // All transforms combined: translate3d, scale, rotate
  opacity: number;
  willChange: string[]; // Properties to optimize
  backfaceVisibility: "visible" | "hidden";
  perspective: number;
}

/**
 * Animation timing and performance configuration
 */
export interface AnimationTimingConfig {
  duration: number;
  easing: string;
  delay: number;
  iterations: number | "infinite";
  direction: "normal" | "reverse" | "alternate" | "alternate-reverse";
  fillMode: "none" | "forwards" | "backwards" | "both";
}

/**
 * Animation performance monitoring
 */
export interface AnimationPerformanceMetrics {
  frameRate: number;
  frameDropCount: number;
  animationLatency: number;
  memoryUsage: number;
  gpuLayersActive: number;
  reducedMotionActive: boolean;
}

/**
 * Animation pooling system for memory efficiency
 */
export interface AnimationPool {
  id: string;
  animations: Map<string, Animation>;
  maxPoolSize: number;
  recycledAnimations: Animation[];
  activeAnimations: Set<string>;
}

// ============================================================================
// COMPONENT-SPECIFIC FEEDBACK PATTERNS
// ============================================================================

/**
 * Chart component specific visual feedback
 */
export interface ChartComponentFeedback {
  dataConnectionIndicator: {
    enabled: boolean;
    position: "bottom-center" | "top-left" | "top-right";
    color: string;
    animationType: "pulse" | "flow";
  };
  miniVisualization: {
    enabled: boolean;
    previewType: "bars" | "line" | "pie" | "scatter";
    opacity: number;
    animateOnHover: boolean;
  };
  dataBinding: {
    enabled: boolean;
    glowEffect: boolean;
    connectionLines: boolean;
  };
}

/**
 * Container component specific visual feedback
 */
export interface ContainerComponentFeedback {
  dropZoneHighlight: {
    enabled: boolean;
    borderStyle: "dashed" | "solid" | "dotted";
    borderColor: string;
    borderOpacity: number;
  };
  containmentIndicator: {
    enabled: boolean;
    message: string;
    messagePosition: "center" | "top" | "bottom";
    backgroundColor: string;
    backgroundOpacity: number;
  };
  childAlignment: {
    enabled: boolean;
    alignmentGuides: boolean;
    spacingIndicators: boolean;
  };
}

/**
 * Layout component specific visual feedback
 */
export interface LayoutComponentFeedback {
  alignmentGuides: {
    enabled: boolean;
    color: string;
    thickness: number;
    magneticSnap: boolean;
    snapDistance: number;
  };
  spacingIndicators: {
    enabled: boolean;
    color: string;
    style: "measurement" | "fill" | "minimal";
    showValues: boolean;
  };
  gridOverlay: {
    enabled: boolean;
    gridSize: number;
    opacity: number;
    color: string;
  };
}

/**
 * Text component specific visual feedback
 */
export interface TextComponentFeedback {
  editingState: {
    enabled: boolean;
    borderColor: string;
    borderStyle: string;
    caretAnimation: boolean;
  };
  formattingIndicators: {
    enabled: boolean;
    toolbarPosition: "above" | "below" | "floating";
    showOnHover: boolean;
    animationDuration: number;
  };
  textFlow: {
    enabled: boolean;
    overflowIndicator: boolean;
    lineHeightGuides: boolean;
  };
}

/**
 * Custom component extensible feedback pattern
 */
export interface CustomComponentFeedback {
  componentType: string;
  customAnimations: Map<string, AnimationConfig>;
  customStates: Map<string, VisualState>;
  extensionPoints: {
    beforeStateChange?: (
      from: VisualFeedbackState,
      to: VisualFeedbackState,
    ) => void;
    afterStateChange?: (state: VisualFeedbackState) => void;
    customValidation?: (action: string) => boolean;
  };
}

// ============================================================================
// ERROR STATE AND RECOVERY PATTERNS
// ============================================================================

/**
 * Error types for visual feedback system
 */
export enum ErrorType {
  INVALID_DROP = "invalid_drop",
  CONSTRAINT_VIOLATION = "constraint_violation",
  NETWORK_ERROR = "network_error",
  PERMISSION_DENIED = "permission_denied",
  COLLISION_DETECTED = "collision_detected",
  SYSTEM_ERROR = "system_error",
}

/**
 * Error visualization configuration
 */
export interface ErrorVisualizationConfig {
  errorType: ErrorType;
  shakeAnimation: {
    enabled: boolean;
    intensity: "subtle" | "moderate" | "strong";
    duration: number;
    frequency: number;
  };
  colorIndicators: {
    enabled: boolean;
    errorColor: string;
    overlayOpacity: number;
    borderHighlight: boolean;
  };
  recoveryGuidance: {
    enabled: boolean;
    tooltip: boolean;
    tooltipText: string;
    suggestedActions: string[];
    autoSuggestAlternatives: boolean;
  };
}

/**
 * Recovery action configuration
 */
export interface RecoveryAction {
  id: string;
  label: string;
  action: () => void | Promise<void>;
  icon?: string;
  priority: "primary" | "secondary" | "tertiary";
  destructive?: boolean;
}

// ============================================================================
// PERFORMANCE OPTIMIZATION TYPES
// ============================================================================

/**
 * Animation batching system for performance
 */
export interface AnimationBatch {
  id: string;
  animations: AnimationRequest[];
  priority: "high" | "normal" | "low";
  batchSize: number;
  processed: boolean;
}

/**
 * Individual animation request
 */
export interface AnimationRequest {
  elementId: string;
  animationType: string;
  properties: GPUAnimationProperties;
  timing: AnimationTimingConfig;
  callback?: () => void;
}

/**
 * Reduced motion accessibility support
 */
export interface ReducedMotionConfig {
  enabled: boolean;
  respectSystemPreferences: boolean;
  fallbackAnimations: Map<string, string>;
  staticAlternatives: Map<string, string>;
  minimumDuration: number; // Minimum animation duration for accessibility
}

// ============================================================================
// INTEGRATION TYPES
// ============================================================================


/**
 * Multi-viewport feedback synchronization
 */
export interface MultiViewportSync {
  enabled: boolean;
  synchronizedStates: VisualFeedbackState[];
  viewportMapping: Map<string, string>; // Source viewport -> target viewports
  delayCompensation: boolean; // Compensate for different viewport update rates
  crossViewportAnimations: boolean;
}

/**
 * Spatial indexing optimization integration
 */
export interface SpatialIndexingOptimization {
  enabled: boolean;
  cullingThreshold: number; // Distance beyond which to disable feedback
  viewportCulling: boolean; // Only show feedback for visible components
  performanceScaling: boolean; // Reduce feedback complexity when many components
  lodSystem: boolean; // Level of detail system for distant components
}

/**
 * Visual feedback context for component rendering
 */
export interface VisualFeedbackContext {
  currentState: VisualFeedbackState;
  transitioningTo?: VisualFeedbackState;
  componentType?: ComponentType;
  position: Position;
  bounds: Rectangle;
  isVisible: boolean;
  performanceLevel: "high" | "medium" | "low";
  userPreferences: {
    reducedMotion: boolean;
    highContrast: boolean;
    touchDevice: boolean;
  };
  isSync?: boolean; // Flag to indicate if this is a multi-viewport sync operation
}

/**
 * Animation configuration interface
 */
export interface AnimationConfig {
  keyframes: Keyframe[];
  options: KeyframeAnimationOptions;
  willChange?: string[];
  fallback?: AnimationConfig; // Fallback for reduced motion
}

/**
 * Visual state definition
 */
export interface VisualState {
  id: string;
  name: string;
  styles: Partial<CSSStyleDeclaration>;
  animations?: AnimationConfig[];
  transitions?: TransitionConfig[];
  triggers?: StateTrigger[];
}

/**
 * Transition configuration between states
 */
export interface TransitionConfig {
  from: string;
  to: string;
  duration: number;
  easing: string;
  delay?: number;
  conditions?: TransitionCondition[];
}

/**
 * State trigger conditions
 */
export interface StateTrigger {
  event: string;
  condition?: (context: VisualFeedbackContext) => boolean;
  action: (context: VisualFeedbackContext) => void;
}

/**
 * Transition condition evaluation
 */
export interface TransitionCondition {
  property: string;
  operator: "==" | "!=" | ">" | "<" | ">=" | "<=";
  value: any;
}

// ============================================================================
// ACCESSIBILITY AND CROSS-BROWSER TYPES
// ============================================================================

/**
 * Accessibility compliance configuration
 * Enhanced for WCAG 2.2 AA compliance and agricultural context
 */
export interface AccessibilityConfig {
  // WCAG 2.2 Compliance
  wcag22AA: boolean;
  colorContrastMinimum: number; // Minimum contrast ratio (4.5:1 for AA)
  colorContrastEnhanced: number; // Enhanced contrast ratio (7:1 for AAA)

  // Motion and Animation
  reducedMotionDetection: boolean;
  reducedMotionSupport: boolean;
  respectSystemPreferences: boolean;

  // Screen Reader Support
  screenReaderSupport: boolean;
  screenReaderAnnouncements: boolean;
  ariaLabels: Map<string, string>;

  // Keyboard Navigation
  keyboardNavigationSupport: boolean;
  focusManagement: boolean;

  // High Contrast Support
  highContrastSupport: boolean;

  // Agricultural Context Optimizations
  gloveCompatibility: boolean; // Larger touch targets for gloved hands
  outdoorVisibility: boolean; // Enhanced visibility for outdoor conditions
  weatherResistantMode: boolean; // High contrast for difficult weather

  // Touch and Mobile
  touchTargetMinimum: number; // Minimum touch target size (44px WCAG 2.2)
  touchTargetSpacing: number; // Minimum spacing between targets

  // Visual Indicators
  visualFocusIndicators: boolean;
  persistentVisualCues: boolean; // For agricultural equipment interface
  emergencyHighVisibility: boolean; // Critical alert mode
}

/**
 * Cross-browser compatibility configuration
 */
export interface CrossBrowserConfig {
  targetBrowsers: string[]; // Browser versions to support
  polyfillsRequired: string[];
  fallbackStrategies: Map<string, string>;
  performanceBaselines: Map<string, AnimationPerformanceSpec>;
}

// ============================================================================
// EXPORT MAIN INTERFACE
// ============================================================================

/**
 * Main Visual Feedback System Interface
 * Orchestrates all aspects of the four-state visual feedback system
 */
export interface VisualFeedbackSystem {
  // State Management
  currentState: VisualFeedbackState;
  stateConfig: {
    empty: EmptyStateConfig;
    ready: ReadyStateConfig;
    active: ActiveStateConfig;
    success: SuccessStateConfig;
    ghostIndicator: GhostIndicatorConfig; // Ghost drop indicator configuration
  };

  // Animation Framework
  animationFramework: {
    performanceSpec: AnimationPerformanceSpec;
    animationPool: AnimationPool;
    batchProcessor: AnimationBatch[];
    performanceMetrics: AnimationPerformanceMetrics;
  };

  // Component-Specific Patterns
  componentPatterns: {
    chart: ChartComponentFeedback;
    container: ContainerComponentFeedback;
    layout: LayoutComponentFeedback;
    text: TextComponentFeedback;
    custom: Map<string, CustomComponentFeedback>;
  };

  // Error Handling
  errorSystem: {
    visualizationConfig: Map<ErrorType, ErrorVisualizationConfig>;
    recoveryActions: Map<ErrorType, RecoveryAction[]>;
  };

  // Performance & Accessibility
  performance: {
    reducedMotion: ReducedMotionConfig;
    accessibility: AccessibilityConfig;
    crossBrowser: CrossBrowserConfig;
  };

  // Integration
  integration: {
    multiViewport: MultiViewportSync;
    spatialIndexing: SpatialIndexingOptimization;
  };

  // Methods
  setState(
    state: VisualFeedbackState,
    context: VisualFeedbackContext,
  ): Promise<void>;
  playAnimation(animationId: string, element: HTMLElement): Promise<void>;
  handleError(error: ErrorType, context: VisualFeedbackContext): Promise<void>;
  optimizePerformance(): void;
  validateAccessibility(): boolean;
}

// ============================================================================
// ADDITIONAL INTEGRATION TYPES
// ============================================================================

/**
 * Multi-viewport synchronization configuration
 */
export interface MultiViewportSync {
  enabled: boolean;
  synchronizedStates: VisualFeedbackState[];
  viewportMapping: Map<string, string>;
  delayCompensation: boolean;
  crossViewportAnimations: boolean;
}

/**
 * Spatial indexing optimization configuration
 */
export interface SpatialIndexingOptimization {
  enabled: boolean;
  cullingThreshold: number;
  viewportCulling: boolean;
  performanceScaling: boolean;
  lodSystem: boolean;
}
